import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../core/services/api.service';
import { CenterService } from '../../../core/services/center.service';
import { PageTitleService } from '../../../core/services/page-title.service';
import { AppDatePipe } from '../../../core/pipes/app-date.pipe';

type ClaimStatus = 'all' | 'pending' | 'allocated' | 'fulfilled' | 'cancelled';

@Component({
  selector: 'app-book-claims',
  standalone: true,
  imports: [CommonModule, FormsModule, AppDatePipe],
  templateUrl: './book-claims.component.html',
  styleUrl: './book-claims.component.scss',
})
export class BookClaimsComponent implements OnInit {

  // ── List state ────────────────────────────────────────────────────────────
  claims: any[] = [];
  isLoading = false;
  activeTab: ClaimStatus = 'pending';
  readonly tabs: { key: ClaimStatus; label: string }[] = [
    { key: 'pending',   label: 'Pending' },
    { key: 'allocated', label: 'Allocated' },
    { key: 'fulfilled', label: 'Fulfilled' },
    { key: 'cancelled', label: 'Cancelled' },
    { key: 'all',       label: 'All' },
  ];

  // ── Add Claim modal ───────────────────────────────────────────────────────
  showAddModal = false;

  // Step 1 — member search
  memberSearch        = '';
  allMembers: any[]   = [];
  memberResults: any[] = [];
  memberSearchError   = '';
  memberSearchIsExpiry = false;
  selectedMember: any = null;

  // Step 2 — book search
  bookSearch      = '';
  bookSearchType: 'name' | 'author' = 'name';
  bookResults: any[] = [];
  isBookSearching = false;
  bookSearchError = '';
  selectedBook: any = null;
  bookQueue: any[]      = [];
  copiesAtCurrentCenter = 0;
  addRemarks = '';

  isAddSaving = false;
  addError    = '';

  // ── Allocate modal ────────────────────────────────────────────────────────
  showAllocateModal   = false;
  allocatingClaim: any = null;
  availableCopies: any[] = [];
  bookQueue2: any[]   = [];
  selectedCopyNo: number | null = null;
  isAllocating = false;
  allocateError = '';

  // ── Cancel modal ──────────────────────────────────────────────────────────
  showCancelModal  = false;
  cancellingClaim: any = null;
  cancelReason = '';
  isCancelling = false;
  cancelError  = '';

  constructor(
    private api: ApiService,
    private centerSvc: CenterService,
    private pageTitle: PageTitleService,
  ) {}

  ngOnInit(): void {
    this.pageTitle.set('Book Claims', 'Manage member book reservation claims');
    this.loadClaims();
    this.api.getMembers({ isActive: 'true' }).subscribe({
      next: (d) => { this.allMembers = d || []; },
      error: () => {},
    });
  }

  // ── List ──────────────────────────────────────────────────────────────────

  loadClaims(): void {
    this.isLoading = true;
    const params = this.activeTab === 'all' ? {} : { status: this.activeTab };
    this.api.getBookClaims(params).subscribe({
      next: (d) => { this.claims = d || []; this.isLoading = false; },
      error: () => { this.isLoading = false; },
    });
  }

  switchTab(tab: ClaimStatus): void {
    this.activeTab = tab;
    this.loadClaims();
  }

  statusBadgeClass(status: string): string {
    return ({
      pending:   'badge bg-warning text-dark',
      allocated: 'badge bg-primary',
      fulfilled: 'badge bg-success',
      cancelled: 'badge bg-secondary',
    } as any)[status] || 'badge bg-secondary';
  }

  statusLabel(status: string): string {
    return ({
      pending:   'Pending',
      allocated: 'Allocated',
      fulfilled: 'Fulfilled',
      cancelled: 'Cancelled',
    } as any)[status] || status;
  }

  // ── Add Claim ─────────────────────────────────────────────────────────────

  openAddModal(): void {
    this.memberSearch = ''; this.memberResults = []; this.memberSearchError = ''; this.memberSearchIsExpiry = false; this.selectedMember = null;
    this.bookSearch = ''; this.bookSearchType = 'name'; this.bookResults = [];
    this.selectedBook = null; this.bookQueue = []; this.copiesAtCurrentCenter = 0;
    this.addRemarks = ''; this.addError = ''; this.isAddSaving = false;
    this.showAddModal = true;
  }

  closeAddModal(): void { this.showAddModal = false; }

  private isMemberActive(m: any): boolean {
    return (m.status || '').toUpperCase() === 'MEMBER';
  }

  searchMembers(): void {
    const q = this.memberSearch.toLowerCase().trim();
    if (!q) {
      this.memberResults       = [];
      this.memberSearchError   = '';
      this.memberSearchIsExpiry = false;
      return;
    }

    const matched = this.allMembers.filter(m =>
      (m.name || '').toLowerCase().includes(q) ||
      String(m.memberSrNo || '').includes(q) ||
      (m.memNo || '').includes(q) ||
      (m.phone || '').includes(q)
    );

    const active   = matched.filter(m =>  this.isMemberActive(m));
    const inactive = matched.filter(m => !this.isMemberActive(m));

    this.memberResults = active.slice(0, 15);

    if (matched.length === 0) {
      this.memberSearchError   = 'No member with these details exists.';
      this.memberSearchIsExpiry = false;
    } else if (active.length === 0 && inactive.length > 0) {
      const names = inactive.slice(0, 3).map(m => m.name).join(', ');
      this.memberSearchError   = `Membership closed — ${names}`;
      this.memberSearchIsExpiry = true;
    } else {
      this.memberSearchError   = '';
      this.memberSearchIsExpiry = false;
    }
  }

  selectMemberForClaim(m: any): void {
    this.selectedMember      = m;
    this.memberResults       = [];
    this.memberSearchError   = '';
    this.memberSearchIsExpiry = false;
    this.memberSearch        = m.name;
  }

  searchBooks(): void {
    const q = this.bookSearch.trim();
    if (!q) return;
    this.isBookSearching = true;
    this.bookSearchError = '';
    this.api.getBooks({ search: q }).subscribe({
      next: (d) => {
        let results = d || [];
        if (this.bookSearchType === 'author') {
          const ql = q.toLowerCase();
          results = results.filter(b =>
            (b.authorNames || []).some((a: string) => a.toLowerCase().includes(ql)) ||
            (b.authorNamesEnglish || []).some((a: string) => a.toLowerCase().includes(ql))
          );
        }
        this.bookResults = results.slice(0, 20);
        this.isBookSearching = false;
      },
      error: (err: any) => {
        this.isBookSearching = false;
        this.bookSearchError = err?.message || 'Search failed — please check your connection and try again.';
      },
    });
  }

  selectBook(book: any): void {
    this.selectedBook         = book;
    this.bookResults          = [];
    this.copiesAtCurrentCenter = 0;
    const centerId = this.centerSvc.selectedCenter?._id || '';
    this.api.getClaimsForBook(book.bookId).subscribe({
      next: (d) => { this.bookQueue = d || []; },
      error: () => { this.bookQueue = []; },
    });
    // Check availability specifically at the current center
    this.api.getAvailableCopiesForClaim(book.bookId, centerId).subscribe({
      next: (copies) => { this.copiesAtCurrentCenter = (copies || []).length; },
      error: () => { this.copiesAtCurrentCenter = 0; },
    });
  }

  submitAddClaim(): void {
    if (!this.selectedMember || !this.selectedBook) {
      this.addError = 'Please select both a member and a book.'; return;
    }
    this.isAddSaving = true;
    this.addError    = '';
    this.api.addBookClaim({
      memberId: this.selectedMember._id,
      bookId:   this.selectedBook.bookId,
      centerId: this.centerSvc.selectedCenter?._id || '',
      remarks:  this.addRemarks.trim(),
    }).subscribe({
      next: () => {
        this.isAddSaving = false;
        this.closeAddModal();
        this.loadClaims();
      },
      error: (err: any) => {
        this.isAddSaving = false;
        this.addError    = err?.message || 'Failed to add claim.';
      },
    });
  }

  // ── Allocate ──────────────────────────────────────────────────────────────

  openAllocateModal(claim: any): void {
    this.allocatingClaim  = claim;
    this.selectedCopyNo   = null;
    this.allocateError    = '';
    this.availableCopies  = [];
    this.bookQueue2       = [];
    this.showAllocateModal = true;

    this.api.getAvailableCopiesForClaim(claim.bookId).subscribe({
      next: (d) => { this.availableCopies = d || []; },
      error: () => {},
    });
    this.api.getClaimsForBook(claim.bookId).subscribe({
      next: (d) => { this.bookQueue2 = d || []; },
      error: () => {},
    });
  }

  closeAllocateModal(): void { this.showAllocateModal = false; this.allocatingClaim = null; }

  queuePositionOf(claimId: string): number {
    const idx = this.bookQueue2.findIndex(c => c.claimId === claimId);
    return idx + 1;
  }

  confirmAllocate(): void {
    if (!this.selectedCopyNo || !this.allocatingClaim) {
      this.allocateError = 'Please select a copy to allocate.'; return;
    }
    this.isAllocating  = true;
    this.allocateError = '';
    this.api.allocateClaim(this.allocatingClaim.claimId, this.selectedCopyNo).subscribe({
      next: () => {
        this.isAllocating = false;
        this.closeAllocateModal();
        this.loadClaims();
      },
      error: (err: any) => {
        this.isAllocating  = false;
        this.allocateError = err?.message || 'Allocation failed.';
      },
    });
  }

  // ── Cancel ────────────────────────────────────────────────────────────────

  openCancelModal(claim: any): void {
    this.cancellingClaim = claim;
    this.cancelReason    = '';
    this.cancelError     = '';
    this.showCancelModal = true;
  }

  closeCancelModal(): void { this.showCancelModal = false; this.cancellingClaim = null; }

  confirmCancel(): void {
    if (!this.cancellingClaim) return;
    this.isCancelling = true;
    this.cancelError  = '';
    this.api.cancelClaim(this.cancellingClaim.claimId, this.cancelReason).subscribe({
      next: () => {
        this.isCancelling = false;
        this.closeCancelModal();
        this.loadClaims();
      },
      error: (err: any) => {
        this.isCancelling = false;
        this.cancelError  = err?.message || 'Cancellation failed.';
      },
    });
  }
}
